import { useState } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Menu } from 'lucide-react'

import {
  AUTHENTICATED_NAV_ITEMS,
  PUBLIC_NAV_ITEMS,
  SUBSCRIPTION_NAV_ITEM,
  isNavItemActive,
  isNavItemVisible,
  resolveNavItemHref,
} from '@/constants/user-menu'
import { STUDIO_NAV_ITEM } from '@/constants/user-menu'
import { isStudioPortalEnabled } from '@/constants/tenant-features'
import { useAuth, useTenantFeatures, usePortalVisibility } from '@/hooks'
import { ContestNavLink } from '@/features/contests/components/ContestNavLink'
import { StudioNavLink } from '@/features/studio/components/StudioNavLink'
import { LangSwitcher } from './LangSwitcher'
import { SiteBrandMark } from './SiteBrandMark'
import { SiteMobileNav } from './SiteMobileNav'
import { SubscriptionNavLink } from './SubscriptionNavLink'
import { UserMenu } from './UserMenu'
import { NotificationBell } from '@/features/messages/components/NotificationBell'
import { Button } from '@/shared/components/ui/button'

function HeaderNavLink({ item }: { item: (typeof PUBLIC_NAV_ITEMS)[number] }) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const active = isNavItemActive(pathname, item)
  const href = resolveNavItemHref(item)

  return (
    <NavLink
      to={href}
      end={item.end}
      className={clsx('portal-nav-link', active && 'is-active')}
    >
      <span className="portal-nav-link__text">{t(item.i18nKey)}</span>
    </NavLink>
  )
}

export function SiteHeader() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { features, isEnabled } = useTenantFeatures()
  const { isPortalEnabled } = usePortalVisibility()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const visible = (item: (typeof PUBLIC_NAV_ITEMS)[number]) =>
    isNavItemVisible(item, isEnabled, isPortalEnabled)

  const publicItems = PUBLIC_NAV_ITEMS.filter(visible)
  const authedItems = AUTHENTICATED_NAV_ITEMS.filter(
    (item) => item.to !== STUDIO_NAV_ITEM.to && visible(item),
  )
  const showStudioNav = isStudioPortalEnabled(features)
  const showSubscriptionNav = isNavItemVisible(SUBSCRIPTION_NAV_ITEM, isEnabled, isPortalEnabled)

  return (
    <>
      <header className="portal-header">
        <SiteBrandMark variant="portal" />

        <nav className="portal-nav" aria-label={t('common.siteNav')}>
          {publicItems.map((item) =>
            item.to === '/contests' ? (
              <ContestNavLink key={item.to} />
            ) : (
              <HeaderNavLink key={item.to} item={item} />
            ),
          )}
          {(authedItems.length > 0 || showStudioNav) && (
            <>
              <span className="portal-nav-divider" aria-hidden />
              {showStudioNav ? <StudioNavLink /> : null}
              {authedItems.map((item) => (
                <HeaderNavLink key={item.to} item={item} />
              ))}
            </>
          )}
        </nav>

        <div className="portal-header-actions">
          {showSubscriptionNav && <SubscriptionNavLink />}

          <div className="portal-lang-wrap">
            <LangSwitcher />
          </div>

          {isAuthenticated ? (
            <>
              <NotificationBell />
              <UserMenu variant="portal" />
            </>
          ) : (
            <Button asChild variant="portal" className="portal-header-pill portal-header-pill--cta">
              <Link to="/auth/login">
                <span className="portal-header-pill__text">{t('auth.enter')}</span>
              </Link>
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="portal-menu-btn"
            aria-expanded={mobileMenuOpen}
            aria-label={t('common.openMenu')}
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu size={18} />
          </Button>
        </div>
      </header>

      <SiteMobileNav
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        publicItems={publicItems}
        authedItems={authedItems}
        showStudioNav={showStudioNav}
        isAuthenticated={isAuthenticated}
        showSubscriptionNav={showSubscriptionNav}
      />
    </>
  )
}
